import { AgencyConstants } from 'features/Agency/agencyHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormTextArea from 'general/components/OtherKeenComponents/Forms/KTFormTextArea';
import KTRadio, {
    KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

AgencySaleCreateAppendixView.propTypes = {
    title: PropTypes.string,
    contentEl: PropTypes.element,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    appendixDetail: PropTypes.object,
    defaultAppendixType: PropTypes.string,
    appendicesList: PropTypes.array,
};

function AgencySaleCreateAppendixView({
    title = '',
    contentEl = null,
    onCancel,
    onConfirm,
    appendixDetail = null,
    defaultAppendixType = '',
    appendicesList = [],
}) {
    // MARK: ---- PROPERTIES ----
    const [appendixName, setAppendixName] = useState('');
    const [appendixNumber, setAppendixNumber] = useState('');
    const [appendixDate, setAppendixDate] = useState(moment().format('YYYY-MM-DD'));
    const [description, setDescription] = useState('');
    const [appendixType, setAppendixType] = useState(AgencyConstants.AppendixType.DIGITAL);
    const [digitalAppendixDocumentFile, setDigitalAppendixDocumentFile] = useState(null);
    const [paperAppendixDocumentFile, setPaperAppendixDocumentFile] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (appendixDetail) {
            setAppendixType(appendixDetail?.appendixType);
            setAppendixName(appendixDetail?.appendixName);
            setAppendixNumber(appendixDetail?.appendixNumber);
            setAppendixDate(appendixDetail?.appendixDate);
            setDescription(appendixDetail?.description);
            setDigitalAppendixDocumentFile(appendixDetail?.appendixDocument);
            setPaperAppendixDocumentFile(appendixDetail?.appendixDocument);
        }
    }, [appendixDetail])

    const isDisabled = useMemo(() => {
        return appendixDetail?.status === AgencyConstants.AppendixStatus.COMPLETED;
    }, [appendixDetail])

    // MARK: ---- FUNCTIONS ----

    const uploadAsyncFunction = async (file) => {
        if (file instanceof File) {
            const mediaList = await miscHelper.callApiUploadFile(file);
            if (_.isArray(mediaList) && mediaList.length > 0) {
                return _.first(mediaList)?.relativePath || null;
            }
        }
        return file;
    };

    const processSelectedFiles = async (files) => {
        const paths = [];
        for (const file of files) {
            const path = await uploadAsyncFunction(file);
            if (path) paths.push(path);
        }
        selectFilePathList.current = paths;
    };

    const handleConfirm = async () => {
        if (!appendixName || !appendixDate || !appendixNumber || !description) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        if (appendixType === AgencyConstants.AppendixType.DIGITAL && !digitalAppendixDocumentFile) {
            ToastHelper.showError('Vui lòng tải lên phụ lục hợp đồng định dạng word');
            return;
        }

        if (appendixType === AgencyConstants.AppendixType.PAPER && !paperAppendixDocumentFile) {
            ToastHelper.showError('Vui lòng tải lên phụ lục hợp đồng scan');
            return;
        }

        if (!appendixDetail && appendicesList?.some(appendix => appendix?.appendixNumber === appendixNumber)) {
            ToastHelper.showError('Số phụ lục đã được sử dụng, vui lòng chọn số phụ lục khác');
            return;
        }

        setLoading(true);

        const param = {
            index: appendixDetail?.index ?? (appendicesList?.length ? appendicesList.length + 1 : 1),
            appendixType,
            appendixName,
            appendixNumber,
            appendixDate,
            description,
            appendixDocument: '',
        };

        if (appendixType === AgencyConstants.AppendixType.PAPER && paperAppendixDocumentFile) {
            param.appendixDocument = await uploadAsyncFunction(paperAppendixDocumentFile);
            param.status = AgencyConstants.AppendixStatus.COMPLETED;
        }

        if (appendixType === AgencyConstants.AppendixType.DIGITAL && digitalAppendixDocumentFile) {
            param.appendixDocument = await uploadAsyncFunction(digitalAppendixDocumentFile);
            param.status = AgencyConstants.AppendixStatus.CREATED;
        }

        const result = onConfirm(param);
        setLoading(result);
    };

    return (
        <div className="">
            <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>{title}</span>
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>{contentEl}</span>
            </div>

            {/* chon loai phu luc */}
            <div
                style={{
                    borderBottom: `1px dashed ${AppColors.gray[300]}`,
                }}
            >
                {!defaultAppendixType?.length && (
                    <div className="radio-inline w-100 px-4 mb-3">
                        <div className="d-flex align-items-center gap-5">
                            <KTRadio
                                name="typeAppendixDigital"
                                label="Phụ lục điện tử"
                                type={KTRadioType.outline}
                                additionalClassName="radio-success"
                                onChange={() => setAppendixType(AgencyConstants.AppendixType.DIGITAL)}
                                checked={appendixType === AgencyConstants.AppendixType.DIGITAL}
                                disabled={!!appendixDetail}
                            />
                            <KTRadio
                                name="typeAppendixPaper"
                                label="Phụ lục giấy"
                                type={KTRadioType.outline}
                                additionalClassName="radio-success"
                                onChange={() => setAppendixType(AgencyConstants.AppendixType.PAPER)}
                                checked={appendixType === AgencyConstants.AppendixType.PAPER}
                                disabled={!!appendixDetail}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="d-flex flex-column gap-3 mt-4">
                {/* so phu luc */}
                <div className="px-4">
                    {UIHelper.buildFormLabel('Tên phụ lục', true)}
                    <KTFormInput
                        name="appendixName"
                        placeholder="Nhập tên phụ lục"
                        type={KTFormInputType.text}
                        value={appendixName}
                        onChange={(value) => setAppendixName(value)}
                        disabled={isDisabled}
                    />
                </div>
                <div className="row m-0 p-0 px-4">
                    {/* so phu luc */}
                    <div className="col-6 pl-0 pr-2">
                        {UIHelper.buildFormLabel('Số phụ lục', true)}
                        <KTFormInput
                            name="appendixNumber"
                            placeholder="Nhập số phụ lục"
                            type={KTFormInputType.text}
                            value={appendixNumber}
                            onChange={(value) => setAppendixNumber(value)}
                            disabled={isDisabled}
                        />
                    </div>
                    {/* ngay phu luc */}
                    <div className="col-6 pl-2 pr-0">
                        {UIHelper.buildFormLabel('Ngày phụ lục', true)}
                        <KTFormInput
                            name="appendixDate"
                            placeholder="Nhập ngày phụ lục"
                            type={KTFormInputType.date}
                            value={appendixDate}
                            onChange={(value) => setAppendixDate(value)}
                            disabled={isDisabled}
                        />
                    </div>
                </div>
                <div className="px-4">
                    {UIHelper.buildFormLabel('Nội dung điều chỉnh, bổ sung', true)}
                    <KTFormTextArea
                        placeholder="Tóm tắt nội dung điều chỉnh, bổ sung"
                        rows={5}
                        name="description"
                        value={description}
                        onChange={(value) => {
                            setDescription(value);
                        }}
                        disabled={isDisabled}
                    />
                </div>
                {/* phu luc hop dong dinh dang word */}
                {appendixType === AgencyConstants.AppendixType.DIGITAL && (
                    <div className='px-6 pb-2'>
                        <div className='row'>
                            <div className='col-6 px-2'>
                                {UIHelper.buildFormLabel('Tải lên phụ lục hợp đồng', true)}
                                <label className="">
                                    {UIHelper.buildNegativeButton(
                                        'Thêm tập tin',
                                        () => { },
                                        <i className="fa-regular fa-upload" />
                                    )}
                                    <input
                                        type="file"
                                        accept={['.pdf']}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file.name.length > 128) {
                                                ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                e.target.value = null;
                                                return;
                                            }
                                            if (file) setDigitalAppendixDocumentFile(file);
                                        }}
                                        hidden
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                    />
                                </label>
                                {digitalAppendixDocumentFile && (
                                    <ImageView
                                        name={digitalAppendixDocumentFile?.lastModified ?? digitalAppendixDocumentFile}
                                        path={_.isString(digitalAppendixDocumentFile) ? digitalAppendixDocumentFile : digitalAppendixDocumentFile?.name}
                                        fileName={
                                            _.isString(digitalAppendixDocumentFile)
                                                ? digitalAppendixDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                : digitalAppendixDocumentFile?.name
                                        }
                                        item={digitalAppendixDocumentFile instanceof File ? digitalAppendixDocumentFile : undefined}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* file phu luc scan */}
                {appendixType === AgencyConstants.AppendixType.PAPER && (
                    <div className='px-6 pb-2'>
                        <div className='row'>
                            <div className='col-6 px-2'>
                                {UIHelper.buildFormLabel('Tải lên phụ lục hợp đồng scan', true)}
                                <label className="">
                                    {UIHelper.buildNegativeButton(
                                        'Thêm tập tin',
                                        () => { },
                                        <i className="fa-regular fa-upload" />
                                    )}
                                    <input
                                        type="file"
                                        accept={['.pdf']}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file.name.length > 128) {
                                                ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                e.target.value = null;
                                                return;
                                            }
                                            if (file) setPaperAppendixDocumentFile(file);
                                        }}
                                        hidden
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                    />
                                </label>
                                {paperAppendixDocumentFile && (
                                    <ImageView
                                        name={paperAppendixDocumentFile?.lastModified ?? paperAppendixDocumentFile}
                                        path={_.isString(paperAppendixDocumentFile) ? paperAppendixDocumentFile : paperAppendixDocumentFile?.name}
                                        fileName={
                                            _.isString(paperAppendixDocumentFile)
                                                ? paperAppendixDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                : paperAppendixDocumentFile?.name
                                        }
                                        item={paperAppendixDocumentFile instanceof File ? paperAppendixDocumentFile : undefined}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* footer */}
            <div
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
                {UIHelper.buildGreenButton('Xác nhận', handleConfirm, null, true, loading)}
            </div>
        </div >
    );
}

export default AgencySaleCreateAppendixView;
